/**
 * petSuv.js — количественный ПЭТ: SUV, SUVpeak, MTV, TLG. Чистая математика,
 * ни одного импорта: проверяется в node на фантоме с заранее известным
 * ответом (tests/petsuv_test.mjs).
 *
 * ПОЧЕМУ ЭТО НУЖНО. Слова владельца: «ПЭТ… PT открывается как картинка».
 * Картинка ПЭТ без SUV — это яркость, которую нельзя сравнить ни с прошлым
 * исследованием, ни с порогом злокачественности: счёт в вокселе зависит от
 * введённой дозы, веса и того, сколько минут прошло от инъекции до съёмки.
 * SUV (standardized uptake value) убирает все три зависимости:
 *
 *     SUVbw [г/мл] = C(t) [Бк/мл] × вес [г] / доза_к_моменту_съёмки [Бк]
 *
 * где доза к моменту съёмки — введённая доза, распавшаяся за время
 * Δt = (время серии − время введения):  доза × 2^(−Δt / T½).
 *
 * Выкладка — по методике QIBA (vendor-neutral pseudocode для SUVbw):
 * фактор считается ИЗ ТЕГОВ, и каждый недостающий тег — это ОТКАЗ С ПРИЧИНОЙ,
 * а не тихая единица вместо множителя. Врач видит, ЧТО именно не найдено.
 *
 * Модуль ничего не знает про DICOM-строки: на входе уже разобранные числа
 * (извлечение из тегов — в src/ui/petPerfusionTools.js, где есть доступ к
 * набору данных). Так модуль остаётся проверяемым числом.
 */

/** 'HHMMSS', 'HHMMSS.ffffff', 'HH:MM:SS' → секунды от полуночи | null. */
export function разобратьВремяDICOM(строка) {
  if (typeof строка !== 'string') return null;
  const s = строка.trim().replace(/:/g, '');
  const м = s.match(/^(\d{2})(\d{2})(\d{2}(?:\.\d+)?)?$/);
  if (!м) return null;
  const ч = Number(м[1]), мин = Number(м[2]), сек = м[3] !== undefined ? Number(м[3]) : 0;
  if (ч > 23 || мин > 59 || сек >= 61) return null;
  return ч * 3600 + мин * 60 + сек;
}

/** 'YYYYMMDD' + время → миллисекунды эпохи UTC | null. Дата нужна, чтобы переход через полночь не дал отрицательное Δt. */
export function датаВремяВМс(дата, время) {
  const сек = разобратьВремяDICOM(время);
  if (сек === null) return null;
  if (typeof дата === 'string' && /^\d{8}$/.test(дата.trim())) {
    const d = дата.trim();
    const г = Number(d.slice(0, 4)), мес = Number(d.slice(4, 6)), день = Number(d.slice(6, 8));
    if (мес >= 1 && мес <= 12 && день >= 1 && день <= 31) {
      return Date.UTC(г, мес - 1, день) + сек * 1000;
    }
  }
  return сек * 1000;   // даты нет — останутся голые секунды (вызывающий это учтёт)
}

/**
 * Фактор пересчёта пикселя в SUVbw.
 *
 * @param {object} п — уже РАЗОБРАННЫЕ значения тегов:
 *   units           — (0054,1001): 'BQML' | 'GML' | 'CNTS' | иное
 *   decayCorrection — (0054,1102): 'START' | 'ADMIN' | 'NONE' | отсутствует
 *   дозаБк          — (0018,1074) RadionuclideTotalDose, Бк
 *   полупериодС     — (0018,1075) RadionuclideHalfLife, с
 *   времяВведения   — (0018,1072) RadiopharmaceuticalStartTime
 *   датаВремяВведения — (0018,1078) RadiopharmaceuticalStartDateTime (если есть — точнее)
 *   датаСерии, времяСерии — (0008,0021)/(0008,0031)
 *   датаСъёмки, времяСъёмки — (0008,0022)/(0008,0032) — запас, если серии нет
 *   весКг           — (0010,1030) PatientWeight
 *   philipsSuv      — (7053,1000): пиксель × это = SUVbw (частный тег Philips)
 *   philipsБкМл     — (7053,1009): пиксель × это = Бк/мл (частный тег Philips)
 * @returns {{ok:true, фактор:number, подробности:object} | {ok:false, причина:string}}
 */
export function факторSUVbw(п = {}) {
  const units = String(п.units || '').trim().toUpperCase();
  const вес = Number(п.весКг);

  // GML: пиксели УЖЕ в г/мл (уже SUVbw после чьей-то обработки) — фактор 1.
  if (units === 'GML') {
    return { ok: true, фактор: 1, подробности: { units, путь: 'пиксели уже в SUVbw (GML)' } };
  }

  // CNTS: счёт без калибровки. Единственный честный путь — частные теги Philips.
  if (units === 'CNTS') {
    const прямой = Number(п.philipsSuv);
    if (Number.isFinite(прямой) && прямой > 0) {
      return { ok: true, фактор: прямой, подробности: { units, путь: 'частный тег Philips (7053,1000): пиксель × фактор = SUVbw' } };
    }
    const вБкМл = Number(п.philipsБкМл);
    if (Number.isFinite(вБкМл) && вБкМл > 0) {
      const остальное = факторSUVbw({ ...п, units: 'BQML' });
      if (!остальное.ok) return остальное;
      return {
        ok: true, фактор: вБкМл * остальное.фактор,
        подробности: { ...остальное.подробности, units, путь: 'Philips (7053,1009) → Бк/мл, дальше как BQML' },
      };
    }
    return { ok: false, причина: 'единицы CNTS (счёт) без частных тегов Philips (7053,1000)/(7053,1009) — в SUV не переводятся' };
  }

  if (units !== 'BQML') {
    return { ok: false, причина: `единицы «${units || 'не указаны'}» (0054,1001) — SUV считается из BQML, GML или CNTS+Philips` };
  }

  // BQML — основной путь.
  if (!Number.isFinite(вес) || вес <= 0) return { ok: false, причина: 'нет веса пациента (0010,1030) — SUVbw без веса не существует' };
  const доза = Number(п.дозаБк);
  if (!Number.isFinite(доза) || доза <= 0) return { ok: false, причина: 'нет введённой дозы (0018,1074) в RadiopharmaceuticalInformationSequence' };
  const полупериод = Number(п.полупериодС);
  if (!Number.isFinite(полупериод) || полупериод <= 0) return { ok: false, причина: 'нет периода полураспада (0018,1075)' };

  const коррекция = String(п.decayCorrection || '').trim().toUpperCase();
  let дельтаС = 0;
  let какСчиталиВремя = '';
  if (коррекция === 'ADMIN') {
    // Значения уже приведены к моменту ВВЕДЕНИЯ — распад учтён производителем.
    дельтаС = 0;
    какСчиталиВремя = 'коррекция ADMIN: распад уже учтён к моменту введения';
  } else if (коррекция === 'START') {
    // Приведено к началу СЪЁМКИ — распад от введения до съёмки считаем сами.
    const времяСъёмки = п.времяСерии ?? п.времяСъёмки;
    const датаСъёмки = п.датаСерии ?? п.датаСъёмки;
    let началоМс = null;
    if (п.датаВремяВведения && /^\d{8}/.test(String(п.датаВремяВведения).trim())) {
      const с = String(п.датаВремяВведения).trim();
      началоМс = датаВремяВМс(с.slice(0, 8), с.slice(8) || '000000');
    }
    if (началоМс === null) началоМс = датаВремяВМс(датаСъёмки, п.времяВведения);
    const съёмкаМс = датаВремяВМс(датаСъёмки, времяСъёмки);
    if (началоМс === null) return { ok: false, причина: 'нет времени введения препарата (0018,1072/1078)' };
    if (съёмкаМс === null) return { ok: false, причина: 'нет времени серии (0008,0031)' };
    дельтаС = (съёмкаМс - началоМс) / 1000;
    if (дельтаС < 0) return { ok: false, причина: `время серии раньше времени введения (Δt = ${дельтаС.toFixed(0)} с) — теги противоречивы, SUV считать нельзя` };
    if (дельтаС > 24 * 3600) return { ok: false, причина: `от введения до серии ${(дельтаС / 3600).toFixed(1)} ч — теги противоречивы` };
    какСчиталиВремя = `Δt = ${(дельтаС / 60).toFixed(1)} мин от введения до серии`;
  } else {
    return { ok: false, причина: `коррекция распада «${коррекция || 'не указана'}» (0054,1102) — честно считается только START или ADMIN` };
  }

  const распад = Math.pow(2, -дельтаС / полупериод);
  const дозаКСъёмке = доза * распад;
  const фактор = (вес * 1000) / дозаКСъёмке;
  return {
    ok: true, фактор,
    подробности: {
      units, коррекция, весКг: вес, дозаМБк: доза / 1e6,
      полупериодМин: полупериод / 60, дельтаМин: дельтаС / 60,
      остатокДозыДоля: распад, дозаКСъёмкеМБк: дозаКСъёмке / 1e6,
      какСчиталиВремя,
      формула: 'SUVbw = пиксель[Бк/мл] × вес[г] / (доза × 2^(−Δt/T½))[Бк]',
    },
  };
}

const _объёмВокселяМл = (spacing) => (spacing[0] * spacing[1] * spacing[2]) / 1000;

/**
 * SUVmax / SUVmean / SUVpeak по маске (или по всему объёму, если маски нет).
 *
 * SUVpeak — среднее по сфере объёмом 1 см³ (радиус 6.204 мм), поставленной
 * так, чтобы среднее было наибольшим; перебираются центры в горячей
 * окрестности максимума (как в PERCIST). На грубой сетке, где в сферу не
 * попадает и пары вокселей, честно возвращается SUVmax с пометкой.
 *
 * @param {Float32Array} скаляры — пиксели в Бк/мл (или уже SUV при фактор=1)
 * @param {object} п — { фактор, dims:[x,y,z], spacing:[мм], маска?: Uint8Array }
 */
export function статистикаSUV(скаляры, { фактор, dims, spacing, маска = null } = {}) {
  if (!скаляры?.length || !Number.isFinite(фактор)) return { ok: false, причина: 'нет данных или фактора' };
  const [dx, dy, dz] = dims || [];
  if (!(dx > 0 && dy > 0 && dz > 0) || dx * dy * dz !== скаляры.length) {
    return { ok: false, причина: 'размеры не сходятся с массивом' };
  }
  let макс = -Infinity, сумма = 0, n = 0, iМакс = -1;
  for (let i = 0; i < скаляры.length; i++) {
    if (маска && !маска[i]) continue;
    const v = скаляры[i];
    if (!Number.isFinite(v)) continue;
    n++; сумма += v;
    if (v > макс) { макс = v; iМакс = i; }
  }
  if (!n) return { ok: false, причина: 'в маске нет ни одного вокселя' };
  const кМакс = [iМакс % dx, Math.floor(iМакс / dx) % dy, Math.floor(iМакс / (dx * dy))];

  // SUVpeak: сфера 1 см³.
  const R = Math.cbrt((3 * 1000) / (4 * Math.PI));   // 6.2035 мм
  const [sx, sy, sz] = spacing;
  const rx = Math.max(1, Math.round(R / sx)), ry = Math.max(1, Math.round(R / sy)), rz = Math.max(1, Math.round(R / sz));
  const средаСферы = (cx, cy, cz) => {
    let с = 0, к = 0;
    for (let z = Math.max(0, cz - rz); z <= Math.min(dz - 1, cz + rz); z++) {
      for (let y = Math.max(0, cy - ry); y <= Math.min(dy - 1, cy + ry); y++) {
        for (let x = Math.max(0, cx - rx); x <= Math.min(dx - 1, cx + rx); x++) {
          const ддx = (x - cx) * sx, ддy = (y - cy) * sy, ддz = (z - cz) * sz;
          if (ддx * ддx + ддy * ддy + ддz * ддz > R * R) continue;
          const i = x + y * dx + z * dx * dy;
          if (маска && !маска[i]) continue;
          const v = скаляры[i];
          if (!Number.isFinite(v)) continue;
          с += v; к++;
        }
      }
    }
    return к ? { среднее: с / к, вокселей: к } : { среднее: -Infinity, вокселей: 0 };
  };
  let пик = -Infinity, пикВокселей = 0;
  // Центры-кандидаты: горячие воксели (≥90% макс) не дальше 12 мм от максимума.
  const охват = [Math.ceil(12 / sx), Math.ceil(12 / sy), Math.ceil(12 / sz)];
  for (let z = Math.max(0, кМакс[2] - охват[2]); z <= Math.min(dz - 1, кМакс[2] + охват[2]); z++) {
    for (let y = Math.max(0, кМакс[1] - охват[1]); y <= Math.min(dy - 1, кМакс[1] + охват[1]); y++) {
      for (let x = Math.max(0, кМакс[0] - охват[0]); x <= Math.min(dx - 1, кМакс[0] + охват[0]); x++) {
        const i = x + y * dx + z * dx * dy;
        if (маска && !маска[i]) continue;
        if (!(скаляры[i] >= 0.9 * макс)) continue;
        const с = средаСферы(x, y, z);
        if (с.среднее > пик) { пик = с.среднее; пикВокселей = с.вокселей; }
      }
    }
  }
  const пикЧестен = пикВокселей >= 2;
  return {
    ok: true,
    suvMax: макс * фактор,
    suvMean: (сумма / n) * фактор,
    suvPeak: (пикЧестен ? пик : макс) * фактор,
    пикВокселей, пикЧестен,
    вокселей: n,
    координатыМакс: кМакс,
  };
}

/**
 * MTV и TLG по порогу.
 *
 * MTV (metabolic tumor volume) — объём вокселей с SUV не ниже порога, мл.
 * TLG (total lesion glycolysis) = MTV × SUVmean включённых вокселей.
 * Порог: либо доля от SUVmax (обычно 0.40 — «40% SUVmax»), либо абсолютный
 * SUV (обычно 2.5). Без маски (сегмента) считать MTV по всему объёму
 * бессмысленно — порог зацепит мозг, мочевой пузырь и сердце; поэтому маска
 * обязательна, и это ОТКАЗ, а не предупреждение.
 */
export function mtvTlg(скаляры, { фактор, dims, spacing, маска, порог = { вид: 'долямакс', значение: 0.40 } } = {}) {
  if (!маска) return { ok: false, причина: 'MTV считается внутри сегмента — обведите очаг или постройте маску' };
  const стат = статистикаSUV(скаляры, { фактор, dims, spacing, маска });
  if (!стат.ok) return стат;
  const порогSUV = порог.вид === 'абс' ? Number(порог.значение) : стат.suvMax * Number(порог.значение);
  if (!Number.isFinite(порогSUV) || порогSUV <= 0) return { ok: false, причина: 'порог не число' };
  let n = 0, сумма = 0;
  for (let i = 0; i < скаляры.length; i++) {
    if (!маска[i]) continue;
    const suv = скаляры[i] * фактор;
    if (suv >= порогSUV) { n++; сумма += suv; }
  }
  const мл = n * _объёмВокселяМл(spacing);
  const среднее = n ? сумма / n : 0;
  return { ok: true, mtvМл: мл, tlg: мл * среднее, suvMeanОбъёма: среднее, вокселей: n, порогSUV, suvMax: стат.suvMax };
}
